import { useSyncExternalStore } from 'react'

import type { DataDatabase } from '../offline/data-db'
import { shellRoute } from './routes/root'

/**
 * La base de tournée de **l'équipe connectée** (D22 pt.4) — la porte des écrans.
 *
 * Il y a une base par équipe, et une seule est ouverte : un écran ne peut donc pas lire la
 * tournée, les brouillons, les photos ou la file de l'équipe précédente, parce que sa base n'est
 * pas ouverte. C'est tout ce qui remplace l'accesseur cloisonné d'avant (#158) — plus rien à
 * filtrer, plus rien à estampiller.
 *
 * ⚠️ **Il s'abonne**, là où « Mes contrôles » lisait le nom d'équipe sans s'abonner. C'est la
 * différence qui compte : une tablette peut changer de main **sans rechargement de page** (le
 * départ navigue), et un écran resté sur la base de son montage continuerait de servir les
 * données du sortant — ou lèverait, la base ayant été refermée.
 *
 * ⚠️ **Il s'abonne au porteur, pas à la session.** `auth.getSnapshot()` rend un objet **neuf** à
 * chaque écriture de session : un rafraîchissement de jeton toutes les quinze minutes (M23),
 * chaque bascule en ligne / dégradé. S'y abonner ferait relire tout l'écran en pleine inspection
 * pour une base qui n'a pas bougé. Le porteur, lui, ne prévient que d'un changement de base.
 *
 * ⚠️ **Le hook vit ici, dans `app/`, et pas dans `offline/`** : il lit le contexte de route, donc
 * `app/routes/root`. Le placer dans `offline/` ferait dépendre la couche la plus basse du terrain
 * du routeur et de la session.
 */
export function useTeamData(): DataDatabase {
  const { data } = shellRoute.useRouteContext()
  const db = useSyncExternalStore(data.subscribe, data.current)

  if (db === null) {
    // Cas impossible sous la coquille : sa garde attend la lecture de la session locale et
    // renvoie vers l'écran de connexion si personne n'est entré (`shellRoute.beforeLoad`) — une
    // session lue nomme toujours une équipe, fût-ce « sans équipe » (M6). Échouer franchement
    // vaut mieux qu'un écran vide qui laisserait croire à une tournée non descendue.
    throw new Error(
      "Aucune base de tournée ouverte : cet écran est monté hors de la coquille, ou sans session lue. Il n'y a rien à lire, et servir une liste vide le ferait passer pour une tournée absente.",
    )
  }

  return db
}
